/**
 * 🎛️ Control - Ohjauspaneeli
 *
 * Listaa kaikki releet suorina kytkiminä. Pohjakuva (Koti-sivu) on hyvä
 * silloin kun tietää missä valaisin fyysisesti on; tämä sivu on nopeaan
 * ohjaukseen ja kokonaiskuvaan: kaikki releet kerralla näkyvissä, haku,
 * ryhmittely tyypin mukaan ja "kaikki pois" -toiminto.
 *
 * Releiden nimet johdetaan Koti-sivun layoutista (localStorage), jossa
 * lampuille, LED-nauhoille ja seinävaloille on määritelty relayId.
 * Nimeämätön rele näkyy silti - sitä voi silti ohjata.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Divider, InputAdornment,
  Paper, Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSocketService } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { onUpdateStatusChange, getAllRelayStatus } from '../helpers/socketHelper';

const LAYOUT_STORAGE_KEY = 'homeLayout:v7';

type RelayKind = 'lamp' | 'strip' | 'wall' | 'unknown';

interface RelayItem {
  relay: number;
  stat: number;
  kind: RelayKind;
  name: string;
}

const KIND_META: Record<RelayKind, { label: string; icon: React.ReactElement }> = {
  lamp:    { label: 'Valaisin',   icon: <LightbulbIcon fontSize="small" /> },
  strip:   { label: 'LED-nauha',  icon: <LinearScaleIcon fontSize="small" /> },
  wall:    { label: 'Seinävalo',  icon: <WbIncandescentIcon fontSize="small" /> },
  unknown: { label: 'Nimeämätön', icon: <HelpOutlineIcon fontSize="small" /> },
};

/** Lukee Koti-sivun layoutin ja rakentaa relayId -> {tyyppi, nimi} -kartan. */
function readRelayNames(): Map<number, { kind: RelayKind; name: string }> {
  const map = new Map<number, { kind: RelayKind; name: string }>();
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return map;
    const data = JSON.parse(raw) as {
      lamps?: { id?: string; relayId?: number }[];
      strips?: { id?: string; relayId?: number }[];
      wallLights?: { id?: string; relayId?: number }[];
    };
    const add = (arr: { id?: string; relayId?: number }[] | undefined, kind: RelayKind, prefix: string) => {
      (arr ?? []).forEach((item, i) => {
        if (typeof item?.relayId === 'number') {
          map.set(item.relayId, { kind, name: item.id ? `${prefix} ${item.id}` : `${prefix} ${i + 1}` });
        }
      });
    };
    add(data.lamps, 'lamp', 'Valaisin');
    add(data.strips, 'strip', 'LED-nauha');
    add(data.wallLights, 'wall', 'Seinävalo');
  } catch {
    // Rikkinäinen layout ei saa kaataa ohjaussivua - releet näkyvät nimettöminä
  }
  return map;
}

export const Control: React.FC = () => {
  const { service, isConnected } = useSocketService();
  const { isAuthenticated } = useAuth();

  const [relays, setRelays] = useState<{ relay: number; stat: number }[]>([]);
  const [names, setNames] = useState(() => readRelayNames());
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<RelayKind | 'all'>('all');
  const [pending, setPending] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Reaaliaikainen tila: alkuarvo + päivitykset statusUpdatesta
  useEffect(() => {
    setRelays(getAllRelayStatus());
    const off = onUpdateStatusChange(() => setRelays(getAllRelayStatus()));
    return off;
  }, []);

  // Layout voi muuttua Koti-sivulla; päivitä nimet kun palataan tälle sivulle
  useEffect(() => {
    const onFocus = () => setNames(readRelayNames());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const items: RelayItem[] = useMemo(() => {
    return relays
      .map((r) => {
        const meta = names.get(r.relay);
        return {
          relay: r.relay,
          stat: r.stat,
          kind: meta?.kind ?? 'unknown',
          name: meta?.name ?? `Rele ${r.relay}`,
        };
      })
      .sort((a, b) => a.relay - b.relay);
  }, [relays, names]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (kindFilter !== 'all' && it.kind !== kindFilter) return false;
      if (!q) return true;
      return it.name.toLowerCase().includes(q) || String(it.relay).includes(q);
    });
  }, [items, search, kindFilter]);

  const onCount = items.filter((i) => i.stat === 1).length;

  const setRelay = useCallback(
    async (relay: number, next: 0 | 1) => {
      if (!isConnected) {
        setToast({ msg: 'Ei yhteyttä palvelimeen', ok: false });
        return;
      }
      setPending((p) => new Set(p).add(relay));

      // Optimistinen päivitys - oikea tila varmistuu seuraavassa statusUpdatessa
      setRelays((prev) => prev.map((r) => (r.relay === relay ? { ...r, stat: next } : r)));

      try {
        const response = await service.sendRequest({
          type: 'controller_command',
          data: { id: 'relays', function: 'setRelay', relay: String(relay), stat: String(next) },
          token: localStorage.getItem('authToken') || undefined,
        });
        if (!response.success) {
          setRelays(getAllRelayStatus()); // peruuta optimistinen muutos
          setToast({ msg: response.error || 'Komento epäonnistui', ok: false });
        }
      } catch (err) {
        setRelays(getAllRelayStatus());
        setToast({ msg: err instanceof Error ? err.message : 'Verkkovirhe', ok: false });
      } finally {
        setPending((p) => {
          const n = new Set(p);
          n.delete(relay);
          return n;
        });
      }
    },
    [isConnected, service]
  );

  const allOff = useCallback(async () => {
    const on = filtered.filter((i) => i.stat === 1);
    if (!on.length) return;
    if (!confirm(`Sammutetaanko ${on.length} päällä olevaa?`)) return;
    for (const it of on) await setRelay(it.relay, 0);
    setToast({ msg: `${on.length} sammutettu`, ok: true });
  }, [filtered, setRelay]);

  if (!isConnected) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Yhdistetään palvelimeen…</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700}>Ohjaus</Typography>
        <Typography color="text.secondary">
          Kaikki releet kerralla — {onCount}/{items.length} päällä
        </Typography>
      </Box>

      {!isAuthenticated && (
        <Alert severity="warning">
          Et ole kirjautunut. Ohjauskomennot vaativat kirjautumisen — kytkimet eivät toimi
          ennen sitä.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            placeholder="Hae nimellä tai releen numerolla…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={kindFilter}
            onChange={(_, v) => v && setKindFilter(v)}
            aria-label="Suodata tyypin mukaan"
          >
            <ToggleButton value="all">Kaikki</ToggleButton>
            <ToggleButton value="lamp">Valaisimet</ToggleButton>
            <ToggleButton value="strip">Nauhat</ToggleButton>
            <ToggleButton value="wall">Seinävalot</ToggleButton>
            <ToggleButton value="unknown">Nimeämättömät</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<PowerSettingsNewIcon />}
            onClick={allOff}
            disabled={!filtered.some((i) => i.stat === 1)}
          >
            Sammuta näkyvät
          </Button>
        </Stack>
      </Paper>

      {items.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography fontWeight={600} gutterBottom>Ei releitä näkyvissä</Typography>
          <Typography color="text.secondary">
            Ohjain ei ole vielä lähettänyt tilatietoa. Tarkista että ohjainlaite on yhteydessä.
          </Typography>
        </Paper>
      )}

      {items.length > 0 && filtered.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Haku ei tuottanut tuloksia.</Typography>
        </Paper>
      )}

      {filtered.length > 0 && (
        <Paper variant="outlined">
          <Stack divider={<Divider />}>
            {filtered.map((it) => {
              const busy = pending.has(it.relay);
              return (
                <Stack
                  key={it.relay}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ px: 2, py: 1.25 }}
                >
                  <Badge
                    color={it.stat === 1 ? 'success' : 'default'}
                    variant="dot"
                    overlap="circular"
                  >
                    <Box sx={{ display: 'flex', color: it.stat === 1 ? 'success.main' : 'text.disabled' }}>
                      {KIND_META[it.kind].icon}
                    </Box>
                  </Badge>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap fontWeight={500}>{it.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {KIND_META[it.kind].label} · rele {it.relay}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={it.stat === 1 ? 'Päällä' : 'Pois'}
                    color={it.stat === 1 ? 'success' : 'default'}
                    variant={it.stat === 1 ? 'filled' : 'outlined'}
                  />

                  <Tooltip title={isAuthenticated ? '' : 'Vaatii kirjautumisen'}>
                    <span>
                      <Switch
                        checked={it.stat === 1}
                        disabled={busy || !isAuthenticated}
                        onChange={(e) => setRelay(it.relay, e.target.checked ? 1 : 0)}
                        inputProps={{ 'aria-label': `${it.name} päälle/pois` }}
                      />
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.ok ? 'success' : 'error'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Control;
